import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardBody, Typography } from '@material-tailwind/react';
import { FaArrowLeft, FaFileInvoiceDollar, FaPrint, FaDownload, FaEnvelope, FaWhatsapp, FaUndoAlt } from 'react-icons/fa';

const sections = [
  {
    title: 'What A Monthly Invoice Is',
    body: [
      'A monthly invoice bills an owner for a selected month.',
      'It creates one main owner transaction record plus detail rows for each plate.',
      'The detail rows are used for auditing, previewing, printing, and sharing.'
    ]
  },
  {
    title: 'Before You Post',
    body: [
      'Confirm the owner is invoiceable.',
      'Confirm the selected year and month are correct.',
      'Review all line items before posting.',
      'Reverse an old invoice first if the month already has an active invoice.'
    ]
  },
  {
    title: 'How The Preview Works',
    body: [
      'The preview shows the invoice header, totals, and line-by-line breakdown.',
      'Each line shows the plate number and the period badge.',
      'Prorated rows keep their own period so previous-month billing remains visible.',
      'The preview is the same layout used for print and PDF export.'
    ]
  },
  {
    title: 'How Posting Works',
    body: [
      'Posting creates one owner_trans header row and multiple owner_trans_details rows.',
      'The header stores the summary fields such as description, notes, reference type, and reference id.',
      'The detail rows store plate-level billing information and invoice period.',
      'The owner balance is updated after the invoice is saved.'
    ]
  },
  {
    title: 'What To Look For In The Breakdown',
    body: [
      'Plate number',
      'Period badge',
      'Gross amount',
      'Discount amount',
      'Net amount',
      'Days counted'
    ]
  },
  {
    title: 'Document Actions',
    body: [
      'Download the PDF for records or email attachments.',
      'Print the PDF for physical filing.',
      'Send the document by email.',
      'Share the document through WhatsApp.',
      'Open the transaction page for the full audit trail.'
    ]
  },
  {
    title: 'Reversals',
    body: [
      'A reversed invoice is not a valid active document.',
      'The original invoice is marked as reversed and linked to the reversal transaction.',
      'Always reverse before reposting a corrected invoice.'
    ]
  },
  {
    title: 'Common Issues',
    body: [
      'If an invoice already exists, reverse it first.',
      'If a car appears twice, check whether the periods are different.',
      'If a car is missing, verify its allocation date and invoice eligibility.',
      'If the PDF clips content, review the preview before printing.'
    ]
  }
];

const OwnerMonthlyInvoiceGuide = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-[#fff8f5] to-[#EDE4DA] px-4 py-6 text-slate-800 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-5xl">
        <div className="mb-6 flex items-center justify-between gap-3">
          <Link
            to="/owners/invoices/monthly"
            className="inline-flex items-center gap-2 rounded-md bg-gray-600 px-4 py-2 text-sm font-semibold text-white shadow-none hover:bg-gray-700"
          >
            <FaArrowLeft />
            Back to Monthly Invoices
          </Link>
          <div className="hidden text-sm font-semibold text-slate-500 sm:block">
            Monthly Invoice Guide
          </div>
        </div>

        <Card className="border-0 shadow-xl">
          <CardBody className="p-6 sm:p-8">
            <div className="mb-6 text-center">
              <div className="mx-auto mb-4 inline-flex h-14 w-14 items-center justify-center rounded-full bg-[#FFF1EB] text-[#FF5733]">
                <FaFileInvoiceDollar className="text-2xl" />
              </div>
              <Typography variant="h3" className="text-[#2a2e30]">
                Owner Monthly Invoice Guide
              </Typography>
              <Typography className="mt-2 text-sm text-slate-600">
                How to preview, post, reverse, download, print, email, and share monthly owner invoices.
              </Typography>
            </div>

            <div className="space-y-5">
              {sections.map((section) => (
                <section key={section.title} className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
                  <Typography variant="h6" className="mb-3 text-[#2a2e30]">
                    {section.title}
                  </Typography>
                  {section.title === 'What To Look For In The Breakdown' ? (
                    <ul className="grid grid-cols-1 gap-2 text-sm text-slate-700 sm:grid-cols-2">
                      {section.body.map((item) => (
                        <li key={item} className="rounded-lg bg-slate-50 px-3 py-2">
                          {item}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <ul className="list-disc space-y-2 pl-5 text-sm text-slate-700">
                      {section.body.map((item) => (
                        <li key={item}>{item}</li>
                      ))}
                    </ul>
                  )}
                </section>
              ))}
            </div>

            <div className="mt-6 rounded-xl border border-[#E7D6CA] bg-[#FFF8F5] p-5">
              <Typography variant="h6" className="mb-3 text-[#2a2e30]">
                Quick Actions In The App
              </Typography>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
                <div className="flex items-center gap-3 rounded-lg bg-white px-4 py-3 shadow-sm">
                  <FaDownload className="text-[#FF5733]" />
                  <span className="text-sm font-medium">Download PDF</span>
                </div>
                <div className="flex items-center gap-3 rounded-lg bg-white px-4 py-3 shadow-sm">
                  <FaPrint className="text-[#FF5733]" />
                  <span className="text-sm font-medium">Print Document</span>
                </div>
                <div className="flex items-center gap-3 rounded-lg bg-white px-4 py-3 shadow-sm">
                  <FaEnvelope className="text-[#FF5733]" />
                  <span className="text-sm font-medium">Send Email</span>
                </div>
                <div className="flex items-center gap-3 rounded-lg bg-white px-4 py-3 shadow-sm">
                  <FaWhatsapp className="text-[#FF5733]" />
                  <span className="text-sm font-medium">Share on WhatsApp</span>
                </div>
              </div>
              <div className="mt-4 flex items-center gap-2 text-sm text-slate-600">
                <FaUndoAlt className="text-amber-600" />
                Reverse an invoice first if you need to repost a corrected document.
              </div>
            </div>
          </CardBody>
        </Card>
      </div>
    </div>
  );
};

export default OwnerMonthlyInvoiceGuide;
